import { Star, UserRound, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import PlayerPicker from "../components/common/PlayerPicker";
import { useMyPlayer } from "../context/MyPlayerContext";
import { playerPath } from "../utils/playerLink";

// Header menu for saving which character is "me", so it is highlighted across
// the site. Looking other players up stays on the Player page.
const PlayerFinder = () => {
  const { myPlayer, setMyPlayer } = useMyPlayer();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  useEffect(() => setShow(false), [location.pathname, location.search]);

  useEffect(() => {
    if (!show) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setShow(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setShow(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [show]);

  return (
    <div ref={rootRef} className={`player-finder${myPlayer ? " has-me" : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="player-finder-trigger"
        aria-expanded={show}
        aria-controls={panelId}
        aria-haspopup="dialog"
        onClick={() => setShow((open) => !open)}
      >
        {myPlayer ? (
          <Star size={15} fill="currentColor" aria-hidden="true" />
        ) : (
          <UserRound size={16} aria-hidden="true" />
        )}
        <span className="player-finder-trigger-text">
          {myPlayer || "Set character"}
        </span>
      </button>

      <div
        id={panelId}
        className={`player-finder-panel${show ? " is-open" : ""}`}
        role="dialog"
        aria-modal="false"
        aria-label="Character settings"
        aria-hidden={!show}
      >
        <div className="player-finder-panel-header">
          <div>
            <div className="player-finder-eyebrow">Character</div>
            <div className="player-finder-title">
              {myPlayer || "Set your character"}
            </div>
          </div>
          <button
            type="button"
            className="player-finder-close"
            aria-label="Close character settings"
            onClick={() => {
              setShow(false);
              triggerRef.current?.focus();
            }}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>

        {myPlayer && (
          <div className="player-finder-shortcuts">
            <Link to={playerPath({ player: myPlayer })}>My profile</Link>
            <Link to={playerPath({ player: myPlayer, tab: "gquests" })}>
              My GQ stats
            </Link>
          </div>
        )}

        <div className="player-finder-body">
          <div className="player-finder-label">
            {myPlayer ? "Change character" : "Character name"}
          </div>
          <PlayerPicker
            inputRef={inputRef}
            ariaLabel="Your character name"
            placeholder="Enter at least 3 characters"
            onPick={(name) => {
              setShow(false);
              setMyPlayer(name);
            }}
          />
          <div className="player-finder-hint">
            Saved in this browser and highlighted in rankings.
          </div>
        </div>

        {myPlayer && (
          <button
            type="button"
            className="player-finder-forget"
            onClick={() => setMyPlayer(null)}
          >
            Forget my character
          </button>
        )}
      </div>
    </div>
  );
};

export default PlayerFinder;
